/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import { providers } from "dc-core";
import { overlayEvents } from "overlays/OverlayEvents";
import { apiFactory } from "api/external/ApiFactory";
import { publicApiNames } from "publicApis/constants";
import { getRawConfig, getBottomPanelRect } from "selectors/bottomBoardSelectors";
import { HIDE_PANEL_STATES, BOTTOM_PANEL_MARGIN } from "constants/constants";
import { getVerticalScrollbarSize } from "selectors/pagedViewState";
import { getExpandedPanelWidth } from "utils/SkinnyRailUtils";
import { ZOOM_TRIGGER_TYPE } from "constants/enums";

const isWhiteListedLHP = (stores, id) => {
  const { skipHideAndOpenOnLHP = [] } = getRawConfig(stores) || {};
  return skipHideAndOpenOnLHP.includes(id);
};

const isWhiteListedRHP = (stores, id) => {
  const { skipHideAndOpenOnRHP = [] } = getRawConfig(stores) || {};
  return skipHideAndOpenOnRHP.includes(id);
};

const hidePanelAndBanner = (stores, val, updateAutoHide = false) => {
  hidePanel(stores, val, updateAutoHide);
  hideBanner(stores, val);
};

const hidePanel = (stores, val, updateAutoHide = false) => {
  validateConfigAndShowPanel(!val, stores, shouldShow => {
    if (updateAutoHide) {
      stores.bottomBoard.setEnableAutoHideNShow(shouldShow);
    }
    stores.bottomBoard.setPanelVisibility(shouldShow);
  });
};

const hideBanner = (stores, val) => {
  stores.bottomBoard.setBannerVisibility(!val);
};

export const handleLHPChange = async (boolean, stores) => {
  const method = boolean ? "subscribe" : "unsubscribe";
  const { hideAndOpenOnRHPResize } = getRawConfig(stores) || {};

  const toolsBoardProvider = await providers["tools-board-provider"]();
  toolsBoardProvider[method]("preview", async val => {
    const config = toolsBoardProvider.getConfig() || {};
    const shouldRetainPanel =
      (isWhiteListedLHP(stores, config.id) && (!val || isWhiteListedLHP(stores, val.id))) ||
      (val && isWhiteListedLHP(stores, val.id) && !config.id);

    if (shouldRetainPanel) {
      hideBanner(stores, !val);
      if (val && isWhiteListedLHP(stores, val.id) && !config.id) {
        stores.bottomBoard.deleteFromLhpRhpSkipStates(HIDE_PANEL_STATES.LHP);
        stores.bottomBoard.setEnableAutoHideNShow(true);
      } else {
        stores.bottomBoard.addToLhpRhpSkipStates(HIDE_PANEL_STATES.LHP);
        if (stores.bottomBoard.lhpRhpSkipStates.has(HIDE_PANEL_STATES.RHP)) {
          stores.bottomBoard.setPanelVisibility(false);
          stores.bottomBoard.setEnableAutoHideNShow(false);
        }
      }
      hideAndOpenOnRHPResize && (await handlePanelOverlapRHP(stores));
    } else {
      hidePanelAndBanner(stores, !val, true);
    }
  });
};

export const handleRHPChange = async (boolean, stores) => {
  const method = boolean ? "subscribe" : "unsubscribe";
  const { hideAndOpenOnRHPResize } = getRawConfig(stores) || {};

  const rhpApis = await apiFactory.getExposedApis("preview", [publicApiNames.RHP], true, true);
  rhpApis[publicApiNames.RHP][method](async val => {
    const shouldRetainPanel =
      (val.currentActiveDropin &&
        isWhiteListedRHP(stores, val.currentActiveDropin) &&
        (!val.previousActiveDropin || isWhiteListedRHP(stores, val.previousActiveDropin))) ||
      (val.previousActiveDropin && isWhiteListedRHP(stores, val.previousActiveDropin) && !val.currentActiveDropin);

    if (shouldRetainPanel) {
      hideBanner(stores, !val.previousActiveDropin);
      if (val.previousActiveDropin && isWhiteListedRHP(stores, val.previousActiveDropin) && !val.currentActiveDropin) {
        stores.bottomBoard.deleteFromLhpRhpSkipStates(HIDE_PANEL_STATES.RHP);
        stores.bottomBoard.setEnableAutoHideNShow(true);
      } else {
        stores.bottomBoard.addToLhpRhpSkipStates(HIDE_PANEL_STATES.RHP);
        if (stores.bottomBoard.lhpRhpSkipStates.has(HIDE_PANEL_STATES.LHP)) {
          stores.bottomBoard.setEnableAutoHideNShow(false);
          stores.bottomBoard.setPanelVisibility(false);
        }
      }
      hideAndOpenOnRHPResize && (await handlePanelOverlapRHP(stores));
    } else {
      hidePanelAndBanner(stores, !val.previousActiveDropin, true);
    }
  });
};

const zoomHandler = (stores, value, triggerType) => {
  if ([ZOOM_TRIGGER_TYPE.NAV_BAR, ZOOM_TRIGGER_TYPE.PINCH_ZOOM, ZOOM_TRIGGER_TYPE.KEYBOARD].includes(triggerType)) {
    hidePanelAndBanner(stores, true);
  }
};

const scrollHandler = async (eventName, stores) => {
  if (eventName === "pagesViewScrolled") {
    if (!stores.bottomBoard.scrollStart) {
      // ignore first scroll event to fix the issue of additional scroll event on zoom
      stores.bottomBoard.setScrollStart(Date.now());
    } else {
      stores.bottomBoard.addToHidePanelActiveStates(HIDE_PANEL_STATES.SCROLL);
      stores.bottomBoard.setPanelVisibility(false);
      stores.bottomBoard.setEnableAutoHideNShow(false);
    }
  }

  if (eventName === "pagesViewScrollEnd") {
    stores.bottomBoard.deleteFromHidePanelActiveStates(HIDE_PANEL_STATES.SCROLL);
    stores.bottomBoard.setEnableAutoHideNShow(true);
    stores.bottomBoard.setScrollStart(0); // reset value on scroll end
  }
};

const selectionHandler = async (eventName, stores) => {
  if (eventName === "selectTextToPoint") {
    stores.bottomBoard.addToHidePanelActiveStates(HIDE_PANEL_STATES.SELECTION);
    stores.bottomBoard.setPanelVisibility(false);
    stores.bottomBoard.setEnableAutoHideNShow(false);
  }

  if (eventName === "selectionclear") {
    stores.bottomBoard.deleteFromHidePanelActiveStates(HIDE_PANEL_STATES.SELECTION);
    stores.bottomBoard.setEnableAutoHideNShow(true);
  }
};

const handleZoomChange = (boolean, stores) => {
  const method = boolean ? "on" : "unregister";
  overlayEvents[method]("zoom", (value, triggerType) => zoomHandler(stores, value, triggerType));
};

const handleScrollChange = (boolean, stores) => {
  const method = boolean ? "on" : "unregister";
  overlayEvents[method]("pagesViewScrolled", () => scrollHandler("pagesViewScrolled", stores));
  overlayEvents[method]("pagesViewScrollEnd", () => scrollHandler("pagesViewScrollEnd", stores));
};

const handleSelectionChange = (boolean, stores) => {
  const method = boolean ? "on" : "unregister";
  overlayEvents[method]("selectTextToPoint", selectionHandler.bind(null, "selectTextToPoint", stores));
  overlayEvents[method]("selectionclear", selectionHandler.bind(null, "selectionclear", stores));
};

const handleHideAfterConfig = (val, stores) => {
  const shouldEnable = val > 0;
  stores.bottomBoard.setEnableAutoHideNShow(shouldEnable);
};

export const doesPanelOverlap = async stores => {
  const toolsBoardProvider = await providers["tools-board-provider"]();
  const config = toolsBoardProvider.getConfig() || {};
  const isLHPOpen = !!config.id;

  const rhpApis = await apiFactory.getExposedApis("preview", [publicApiNames.RHP], true, true);
  const rhpConfig = (await rhpApis[publicApiNames.RHP].getConfig()) || {};
  const isRHPOpen = !!rhpConfig.dropinId;

  const availableWidth =
    window.innerWidth -
    (isLHPOpen ? 276 : 0) -
    getVerticalScrollbarSize(stores) -
    (isRHPOpen ? getExpandedPanelWidth(stores) : 0) -
    51; // 276: LHP Width, 51: Skinny Rail Width

  const { panelWidth = 0 } = getRawConfig(stores) || {};
  const requiredWidth = (getBottomPanelRect(stores).width || panelWidth) + BOTTOM_PANEL_MARGIN * 2; // 679: Default FAB Width

  return requiredWidth > availableWidth;
};

const handlePanelOverlapRHP = async stores => {
  if (await doesPanelOverlap(stores)) {
    stores.bottomBoard.addToHidePanelActiveStates(HIDE_PANEL_STATES.OVERLAP);
    stores.bottomBoard.setPanelVisibility(false);
    stores.bottomBoard.setEnableAutoHideNShow(false);
  } else {
    stores.bottomBoard.deleteFromHidePanelActiveStates(HIDE_PANEL_STATES.OVERLAP);
    stores.bottomBoard.setEnableAutoHideNShow(true);
  }
};

export const handleRHPResize = async (boolean, stores) => {
  const method = boolean ? "customEventsSubscribe" : "customEventsUnsubscribe";
  const rhpRef = stores.appStore.rhpRefValue;
  rhpRef.apiService.registeredPublicApis[method](async ({ isRHPResizing }) => {
    if (isRHPResizing) {
      stores.bottomBoard.addToHidePanelActiveStates(HIDE_PANEL_STATES.OVERLAP);
      stores.bottomBoard.setPanelVisibility(false);
      stores.bottomBoard.setEnableAutoHideNShow(false);
    } else if (!(await doesPanelOverlap(stores))) {
      stores.bottomBoard.deleteFromHidePanelActiveStates(HIDE_PANEL_STATES.OVERLAP);
      stores.bottomBoard.setEnableAutoHideNShow(true);
    }
  }, "resizeRHP");
};

export const handleConfigChange = (config, stores) => {
  const handlers = {
    hideAndOpenOnLHP: handleLHPChange,
    hideAndOpenOnRHP: handleRHPChange,
    hideAndOpenOnZoom: handleZoomChange,
    hideAndOpenOnScroll: handleScrollChange,
    hideAndOpenOnRHPResize: handleRHPResize,
    hideOnSelection: handleSelectionChange,
    hideAfter: handleHideAfterConfig
  };

  Object.keys(config).forEach(key => handlers[key] && handlers[key](config[key], stores));
};

export const validateConfigAndShowPanel = async (val, stores, showCallback) => {
  const bottomBoardConfig = getRawConfig(stores);
  const { hideAndOpenOnLHP, hideAndOpenOnRHP, hideAndOpenOnRHPResize } = bottomBoardConfig || {};

  if (!hideAndOpenOnLHP && !hideAndOpenOnRHP && !hideAndOpenOnRHPResize) {
    showCallback(val);
    return;
  }

  const rhpApis = await apiFactory.getExposedApis("preview", [publicApiNames.RHP], true, true);
  rhpApis[publicApiNames.RHP].getConfig().then(async (rhpConfig = {}) => {
    if (hideAndOpenOnRHP && rhpConfig.dropinId && !isWhiteListedRHP(stores, rhpConfig.dropinId)) {
      stores.bottomBoard.addToHidePanelActiveStates(HIDE_PANEL_STATES.RHP);
      stores.bottomBoard.setEnableAutoHideNShow(false);
      val = false;
    } else {
      stores.bottomBoard.deleteFromHidePanelActiveStates(HIDE_PANEL_STATES.RHP);
    }

    if (hideAndOpenOnRHP && rhpConfig.dropinId && isWhiteListedRHP(stores, rhpConfig.dropinId)) {
      stores.bottomBoard.addToLhpRhpSkipStates(HIDE_PANEL_STATES.RHP);
    } else {
      stores.bottomBoard.deleteFromLhpRhpSkipStates(HIDE_PANEL_STATES.RHP);
    }

    if (hideAndOpenOnRHPResize && (await doesPanelOverlap(stores))) {
      stores.bottomBoard.addToHidePanelActiveStates(HIDE_PANEL_STATES.OVERLAP);
      stores.bottomBoard.setEnableAutoHideNShow(false);
      val = false;
    } else {
      stores.bottomBoard.deleteFromHidePanelActiveStates(HIDE_PANEL_STATES.OVERLAP);
    }

    providers["tools-board-provider"]().then(toolsBoardProvider => {
      const lhpConfig = toolsBoardProvider.getConfig() || {};
      if (hideAndOpenOnLHP && lhpConfig.id && !isWhiteListedLHP(stores, lhpConfig.id)) {
        stores.bottomBoard.addToHidePanelActiveStates(HIDE_PANEL_STATES.LHP);
        stores.bottomBoard.setEnableAutoHideNShow(false);
        val = false;
      } else {
        stores.bottomBoard.deleteFromHidePanelActiveStates(HIDE_PANEL_STATES.LHP);
      }

      if (hideAndOpenOnLHP && lhpConfig.id && isWhiteListedLHP(stores, lhpConfig.id)) {
        stores.bottomBoard.addToLhpRhpSkipStates(HIDE_PANEL_STATES.LHP);
      } else {
        stores.bottomBoard.deleteFromLhpRhpSkipStates(HIDE_PANEL_STATES.LHP);
      }

      if (stores.bottomBoard.lhpRhpSkipStates.size === 2) {
        stores.bottomBoard.setEnableAutoHideNShow(false);
        val = false;
      }

      showCallback(val);
    });
  });
};
